import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthenticationService } from 'src/app/Services/auth.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FinancialYear } from 'src/app/Models/accounting-row';
import { ApiService } from 'src/app/Services/api.service';
import { Company } from 'src/app/Models/company';
import { UntypedFormControl } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, map, take } from 'rxjs/operators';

interface PreviewDevice {
  label: string;
  viewportWidth: number;
  viewportHeight: number;
  shellWidth: number;
  shellHeight: number;
  screenInsetX: number;
  screenInsetY: number;
  frameRadius: number;
  screenRadius: number;
  islandWidth: number;
  islandHeight: number;
  homeIndicatorWidth: number;
}

type AdminSegment = 'access' | 'ai' | 'exports' | 'risk';

@Component({
  selector: 'app-app-view-page',
  templateUrl: './app-view-page.component.html',
  styleUrls: ['./app-view-page.component.scss'],
})
export class AppViewPageComponent implements OnInit {
  company: Company;
  companyInfo$: BehaviorSubject<Company> = new BehaviorSubject<Company>(null);
  companyToken: string;
  isAdmin: boolean = this.authentificationService.isAdmin();
  aiiaIntegrationId: string;
  $aiiaIntegrationId: Observable<string> = this.companyInfo$.asObservable().pipe(
    map((company) => {
      const aiiaIntegration = company?.integrations?.find(
        (i) => i.typeTag === 'aiia' && !i.deleted,
      );
      return aiiaIntegration ? aiiaIntegration.id : null;
    }),
  );
  url: SafeResourceUrl;
  financialYears: FinancialYear[];
  financialYearControl: UntypedFormControl = new UntypedFormControl();
  selectedFinancialYear: FinancialYear;
  subscriptionLevel$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  grantPlusExpirationYear: Date = null;
  grantPlusIsTrial: boolean = false;
  passwordResetUrl$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isGrantingPlus = false;
  plusAccessMessage: string | null = null;
  plusAccessError: string | null = null;
  isFetchingPasswordReset = false;
  passwordResetMessage: string | null = null;
  passwordResetError: string | null = null;
  isUpdatingAiChat = false;
  aiChatUpdateMessage: string | null = null;
  aiChatUpdateError: string | null = null;
  activeSegment: AdminSegment = 'ai';
  readonly previewDevice: PreviewDevice = {
    label: 'iPhone Pro',
    viewportWidth: 393,
    viewportHeight: 852,
    shellWidth: 421,
    shellHeight: 880,
    screenInsetX: 14,
    screenInsetY: 14,
    frameRadius: 56,
    screenRadius: 42,
    islandWidth: 126,
    islandHeight: 34,
    homeIndicatorWidth: 134,
  };
  constructor(
    private router: Router,
    private authentificationService: AuthenticationService,
    public sanitizer: DomSanitizer,
    private api: ApiService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.activeSegment = 'ai';
    const decoded = localStorage.getItem('villainCompany');
    this.company = JSON.parse(decoded);
    this.companyInfo$.next(JSON.parse(decoded) as Company);
    this.financialYearControl.valueChanges.subscribe((fy) => {
      this.selectedFinancialYear = fy;
    });
    this.authentificationService
      .getCompanyToken(this.company.id)
      .subscribe((companyToken) => {
        this.companyToken = companyToken.signed;

        this.getFinancialYears();
        this.refreshPreview();
      });
    this.updateCompanyInfo();
  }

  isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
    input !== null && input.tagName === 'IFRAME';

  closeConnectionAndGoBack() {
    this.router.navigateByUrl('search');
  }

  getFinancialYears() {
    this.api
      .getFinancialYears(this.company.id, this.companyToken)
      .subscribe((financialYears: FinancialYear[]) => {
        this.financialYears = financialYears;
      });
  }

  private refreshPreview(): void {
    if (!this.companyToken) {
      return;
    }

    const previewUrl = new URL(`${environment.villainUrl}/login`);
    previewUrl.searchParams.set('villainToken', this.companyToken);
    // Force a fresh iframe load so prod previews do not reuse stale app state.
    previewUrl.searchParams.set('previewRefresh', Date.now().toString());

    this.url = this.sanitizer.bypassSecurityTrustResourceUrl(
      previewUrl.toString(),
    );
  }

  sieExport() {
    if (this.selectedFinancialYear) {
      this.api
        .getSieData(
          this.company.id,
          this.selectedFinancialYear.id,
          this.companyToken,
        )
        .subscribe((data) => {
          console.log(data);
        });
    }
  }

  updateCompanyInfo(): void {
    this.api.getCompany(this.company.id).subscribe({
      next: (company: Company) => {
        this.company = company;
        this.companyInfo$.next(company);
        const aiiaIntegration = company.integrations?.find(
          (i) => i.typeTag === 'aiia' && !i.deleted,
        );
        this.aiiaIntegrationId = aiiaIntegration?.id ?? null;
      },
      error: (err) => console.error(err),
    });

    this.api.getCompanyInfo(this.company.id).subscribe({
      next: (resp: any) => {
        const level = resp?.[0]?.subscription?.level ?? null;
        this.subscriptionLevel$.next(level);
      },
      error: (err) => {
        console.error(err);
        this.subscriptionLevel$.next(null);
      },
    });
  }

  updateGrantIsTrial(event: MatCheckboxChange) {
    this.grantPlusIsTrial = event.checked;
  }

  formatDateToString(date: Date): string {
    let month = date.getMonth() + 1;
    let day = date.getDate();
    let monthStr: string = '';
    let dayStr: string = '';
    if (month < 10) {
      monthStr = '0' + month;
    } else monthStr = month.toString();
    if (day < 10) {
      dayStr = '0' + day;
    } else dayStr = day.toString();
    return date.getFullYear() + '-' + monthStr + '-' + dayStr;
  }

  grantPlusAccess() {
    if (this.grantPlusExpirationYear) {
      const formattedDate: string = this.formatDateToString(
        this.grantPlusExpirationYear,
      );
      this.isGrantingPlus = true;
      this.plusAccessMessage = null;
      this.plusAccessError = null;
      this.api
        .grantUserPlusAccess(
          this.company.id,
          formattedDate,
          this.grantPlusIsTrial,
        )
        .pipe(
          finalize(() => {
            this.isGrantingPlus = false;
          }),
        )
        .subscribe({
          next: () => {
            this.plusAccessMessage = `PLUS-access har getts till och med ${formattedDate}.`;
            this.showConfirmation('PLUS-access har getts.');
            this.updateCompanyInfo();
            this.grantPlusExpirationYear = null;
            this.grantPlusIsTrial = false;
          },
          error: (error) => {
            console.error(error);
            this.plusAccessError =
              error?.error?.message ||
              error?.message ||
              'Kunde inte ge PLUS-access.';
          },
        });
    }
  }

  getPasswordResetLink() {
    const email = this.company?.adminPortalDisplayData?.loginEmail;
    if (!email) return;

    this.isFetchingPasswordReset = true;
    this.passwordResetMessage = null;
    this.passwordResetError = null;
    this.passwordResetUrl$.next(null);

    this.api
      .getUserPasswordResetUrl(email)
      .pipe(
        take(1),
        map((resp: any) =>
          Array.isArray(resp) ? resp[0]?.resetUrl ?? null : resp?.resetUrl ?? null,
        ),
        finalize(() => {
          this.isFetchingPasswordReset = false;
        }),
      )
      .subscribe({
        next: (url) => {
          if (!url) {
            this.passwordResetError = 'Ingen återställningslänk kunde hämtas.';
            return;
          }

          this.passwordResetUrl$.next(url);
          this.passwordResetMessage = 'Återställningslänk hämtad.';
          this.passwordResetError = null;
        },
        error: (err) => {
          console.error(err);
          this.passwordResetError =
            err?.error?.message ||
            err?.message ||
            'Kunde inte hämta återställningslänk.';
        },
      });
  }

  setAiChatEnabled(enabled: boolean) {
    if (!this.company?.id || this.company.aiChatEnabled === enabled) {
      return;
    }

    this.isUpdatingAiChat = true;
    this.aiChatUpdateMessage = null;
    this.aiChatUpdateError = null;

    this.api
      .setCompanyAiChatEnabled(this.company.id, enabled)
      .pipe(
        finalize(() => {
          this.isUpdatingAiChat = false;
        }),
      )
      .subscribe({
        next: (response) => {
          const currentCompanyInfo = this.companyInfo$.getValue() ?? this.company;
          this.company = {
            ...this.company,
            aiChatEnabled: response.aiChatEnabled,
          };
          this.companyInfo$.next({
            ...currentCompanyInfo,
            aiChatEnabled: response.aiChatEnabled,
          } as Company);
          this.aiChatUpdateMessage =
            response.msg ||
            (enabled
              ? 'AI-chatt är nu aktiverad för företaget.'
              : 'AI-chatt är nu avaktiverad för företaget.');
          this.showConfirmation(
            enabled
              ? 'AI-chatt aktiverades.'
              : 'AI-chatt avaktiverades.',
          );
          this.refreshPreview();
          this.updateCompanyInfo();
        },
        error: (error) => {
          console.error(error);
          this.aiChatUpdateError =
            error?.error?.message ||
            error?.message ||
            'Kunde inte uppdatera åtkomst till AI-chatt.';
        },
      });
  }

  get aiChatIsEnabled(): boolean {
    return !!this.company?.aiChatEnabled;
  }

  get hasPlusAccess(): boolean {
    const level = this.subscriptionLevel$.getValue();
    return !!level && level !== 'base' && level !== 'undecided';
  }

  setActiveSegment(segment: AdminSegment) {
    this.activeSegment = segment;
  }

  onSegmentKeydown(event: KeyboardEvent, current: AdminSegment): void {
    const segments: AdminSegment[] = ['ai', 'access', 'exports', 'risk'];
    const currentIndex = segments.indexOf(current);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % segments.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + segments.length) % segments.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = segments.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextSegment = segments[nextIndex];
    this.setActiveSegment(nextSegment);
    document.getElementById(`segment-tab-${nextSegment}`)?.focus();
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(
      () => {
        console.log('Copied!');
        this.showConfirmation('Kopierat.');
      },
      (err) => console.error('Copy failed', err),
    );
  }

  private showConfirmation(message: string) {
    this.snackBar.open(message, 'Stäng', {
      duration: 2600,
      horizontalPosition: 'right',
      verticalPosition: 'top',
    });
  }
}
